import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FileCopy as DuplicateIcon,
  Publish as PublishIcon,
  UnpublishedOutlined as UnpublishIcon,
  Web as PageIcon
} from '@mui/icons-material';
import { PageDefinition, CreatePageRequest } from '../types/pages';
import { PageService } from '../services/pageService';
import PageBuilder from '../components/pages/PageBuilder';
import PagePreview from '../components/pages/PagePreview';

const PageBuilderPage: React.FC = () => {
  const [pages, setPages] = useState<PageDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState<PageDefinition | null>(null);
  const [mode, setMode] = useState<'list' | 'create' | 'edit' | 'preview'>('list');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<PageDefinition | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const pagesData = await PageService.getPages();
      setPages(pagesData.pages);
    } catch (error) {
      console.error('Error loading pages:', error);
      showSnackbar('Error loading pages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreatePage = async (pageData: CreatePageRequest) => {
    try {
      await PageService.createPage(pageData);
      showSnackbar('Page created successfully', 'success');
      loadPages();
      setMode('list');
    } catch (error) {
      console.error('Error creating page:', error);
      showSnackbar('Error creating page', 'error');
    }
  };

  const handleUpdatePage = async (id: string, pageData: Partial<CreatePageRequest>) => {
    try {
      await PageService.updatePage(id, pageData);
      showSnackbar('Page updated successfully', 'success');
      loadPages();
      setMode('list');
    } catch (error) {
      console.error('Error updating page:', error);
      showSnackbar('Error updating page', 'error');
    }
  };

  const handleDeletePage = async () => {
    if (!pageToDelete) return;
    
    try {
      await PageService.deletePage(pageToDelete.id);
      showSnackbar('Page deleted successfully', 'success');
      loadPages();
      setDeleteDialogOpen(false);
      setPageToDelete(null);
    } catch (error) {
      console.error('Error deleting page:', error);
      showSnackbar('Error deleting page', 'error');
    }
  };

  const handleDuplicatePage = async (page: PageDefinition) => {
    try {
      await PageService.duplicatePage(page.id, `${page.name} (Copy)`);
      showSnackbar('Page duplicated successfully', 'success');
      loadPages();
    } catch (error) {
      console.error('Error duplicating page:', error);
      showSnackbar('Error duplicating page', 'error');
    }
  };

  const handleTogglePublish = async (page: PageDefinition) => {
    try {
      if (page.isActive) {
        await PageService.unpublishPage(page.id);
        showSnackbar('Page unpublished', 'success');
      } else {
        await PageService.publishPage(page.id);
        showSnackbar('Page published', 'success');
      }
      loadPages();
    } catch (error) {
      console.error('Error toggling page status:', error);
      showSnackbar('Error updating page status', 'error');
    }
  };

  const renderPagesList = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Pages
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedPage(null);
            setMode('create');
          }}
          sx={{ borderRadius: 2 }}
        >
          Create Page
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pages allow you to publish dynamic content with various layouts.
      </Typography>
      
      {loading ? (
        <Typography>Loading pages...</Typography>
      ) : (
        <Grid container spacing={2.5}>
          {pages.map((page) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={page.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {page.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip 
                        label={page.isActive ? 'Published' : 'Draft'} 
                        color={page.isActive ? 'success' : 'default'}
                        size="small"
                      />
                      {page.isSystem && (
                        <Chip 
                          label="System" 
                          color="info"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 500 }}>
                    {page.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, height: '2.5em', overflow: 'hidden' }}>
                    {page.description || 'No description provided'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip label={page.category} variant="outlined" size="small" />
                    <Chip label={`v${page.version}`} variant="outlined" size="small" />
                    <Chip label={page.route} variant="outlined" size="small" icon={<PageIcon />} />
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    Updated {new Date(page.updatedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <Box sx={{ p: 1.5, pt: 0, display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Preview">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedPage(page);
                        setMode('preview');
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedPage(page);
                        setMode('edit');
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Duplicate">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDuplicatePage(page)}
                    >
                      <DuplicateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  {!page.isSystem && (
                    <Tooltip title={page.isActive ? 'Unpublish' : 'Publish'}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleTogglePublish(page)}
                      >
                        {page.isActive ? <UnpublishIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {!page.isSystem && (
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setPageToDelete(page);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderPageBuilder = () => (
    <PageBuilder
      page={selectedPage}
      onSave={selectedPage ? 
        (pageData) => handleUpdatePage(selectedPage.id, pageData) : 
        handleCreatePage
      }
      onCancel={() => setMode('list')}
    />
  );

  const renderPagePreview = () => (
    selectedPage && (
      <PagePreview
        page={selectedPage}
        onBack={() => setMode('list')}
        onEdit={() => setMode('edit')}
      />
    )
  );

  return (
    <Box>
      {mode === 'list' && renderPagesList()}
      {(mode === 'create' || mode === 'edit') && renderPageBuilder()}
      {mode === 'preview' && renderPagePreview()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{pageToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePage} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageBuilderPage;